import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminCreateFaq, adminGetFaq, adminUpdateFaq } from "@/lib/api";
import { toast } from "sonner";

const empty = { question: "", answer: "", category: "general", order: 0, published: true, references: [] };

export default function FaqEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) adminGetFaq(id).then(setForm).catch(() => toast.error("Not found"));
  }, [id, editing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      if (editing) { await adminUpdateFaq(id, form); toast.success("Saved"); }
      else { const c = await adminCreateFaq(form); toast.success("Created"); navigate("/admin/faqs"); return; }
    } catch (e) {
      if (e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Save failed");
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="faq-editor">
      <Link to="/admin/faqs" className="overline editorial-link">← All FAQs</Link>
      <h1 className="font-serif-editorial tight-track mt-4 mb-10" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1 }}>
        {editing ? "Edit FAQ" : "New FAQ"}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-[900px]">
        <div className="md:col-span-2 space-y-5">
          <div>
            <div className="overline mb-2">Question</div>
            <input type="text" value={form.question} onChange={(e) => update("question", e.target.value)} className="editor-input" data-testid="faq-question" />
          </div>
          <div>
            <div className="overline mb-2">Answer</div>
            <textarea value={form.answer} onChange={(e) => update("answer", e.target.value)} rows={10} className="editor-input" data-testid="faq-answer" />
          </div>
          <div>
            <div className="overline mb-2">References (one per line — appears below the answer)</div>
            <textarea
              value={(form.references || []).join("\n")}
              onChange={(e) => update("references", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
              rows={4}
              className="editor-input"
              data-testid="faq-references"
              placeholder="American College of Gastroenterology (ACG) Clinical Guideline&#10;American Gastroenterological Association (AGA) Clinical Practice Update"
            />
          </div>
        </div>
        <div className="space-y-5">
          <div>
            <div className="overline mb-2">Category</div>
            <select value={form.category} onChange={(e) => update("category", e.target.value)} className="editor-input" data-testid="faq-category">
              {["general", "what-is-sibo", "symptoms", "diagnosis", "treatment", "diet", "recipes", "research"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="overline mb-2">Order (lower = higher)</div>
            <input type="number" value={form.order} onChange={(e) => update("order", parseInt(e.target.value) || 0)} className="editor-input" data-testid="faq-order" />
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} data-testid="faq-published" />
            Published
          </label>
          <button onClick={save} disabled={loading} className="btn-olive w-full" data-testid="save-faq">
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`
        .editor-input {
          width: 100%; padding: 0.65rem 0.9rem;
          border: 1px solid rgba(79,87,70,0.35);
          background: transparent; color: var(--olive);
          font-size: 0.95rem; outline: none;
        }
        .editor-input:focus { border-color: var(--olive); }
      `}</style>
    </div>
  );
}
